import { useEffect, useState } from 'react';
import { apiFetch } from '../auth/api-client';
import { MAX_CHAT_PHOTOS, PHOTOS_PER_MESSAGE } from './api';
import { THREAD_WINDOW } from './thread-window';
import { FRIENDS_DEFAULT, type FriendsConfig } from '../../../../shared/friends-copy.js';

/**
 * Client constants (2026-10-19 Riley): the numbers the admin console sets for
 * every client — GET /api/app/config (edge #edge/config/client-config.js). Read
 * once as the app starts (EmberApp), kept in memory; until it answers, or when
 * it fails, the constants this page always had are the values. Nothing here
 * touches window at module load, so the server render reads the defaults.
 *
 * Meet people's topics, limits and the sentences that say how it works
 * (2026-11-01 Riley) come in the same answer as `friends` (src/shared/friends-copy.js
 * is the default until then). The live channel says "the config changed" with
 * the answer's `rev` (live.ts → reloadClientConfig): a different rev reads it
 * again, and every page using it redraws.
 */

export interface ClientConfig {
  photos_per_message: number;
  photos_per_conversation: number;
  chat_poll_seconds: number;
  thread_window: number;
}

export const CLIENT_CONFIG_DEFAULTS: Readonly<ClientConfig> = Object.freeze({
  photos_per_message: PHOTOS_PER_MESSAGE,
  photos_per_conversation: MAX_CHAT_PHOTOS,
  chat_poll_seconds: 5,
  thread_window: THREAD_WINDOW,
});

let current: ClientConfig = { ...CLIENT_CONFIG_DEFAULTS };
let loading: Promise<ClientConfig> | null = null;
const listeners = new Set<(c: ClientConfig) => void>();
let friends: FriendsConfig = FRIENDS_DEFAULT;
let rev = '';
const friendsListeners = new Set<(f: FriendsConfig) => void>();

/** The values in force now: the edge's once it has answered, the defaults before. */
export function clientConfig(): ClientConfig {
  return current;
}

/** Take an answer's values: a known key with a positive integer wins, anything else keeps the default. */
export function applyClientConfig(values: unknown): ClientConfig {
  const v = values && typeof values === 'object' ? values as Partial<Record<keyof ClientConfig, unknown>> : {};
  const next = { ...CLIENT_CONFIG_DEFAULTS };
  for (const key of Object.keys(next) as (keyof ClientConfig)[]) {
    const n = v[key];
    if (typeof n === 'number' && Number.isInteger(n) && n > 0) next[key] = n;
  }
  current = next;
  for (const fn of listeners) fn(current);
  return current;
}

/** Take an answer's `friends`: each part that came whole wins, anything missing keeps what was there. */
export function applyFriendsConfig(served: unknown): FriendsConfig {
  const f = served && typeof served === 'object' ? served as Partial<FriendsConfig> : {};
  const topics = Array.isArray(f.topics)
    ? f.topics.filter((t) => t && typeof t.label === 'string' && t.label.trim() && typeof t.tint === 'string')
    : [];
  const limits = { ...FRIENDS_DEFAULT.limits };
  for (const key of Object.keys(limits) as (keyof FriendsConfig['limits'])[]) {
    const n = f.limits?.[key];
    if (typeof n === 'number' && Number.isInteger(n) && n > 0) limits[key] = n;
  }
  const words = { ...FRIENDS_DEFAULT.words };
  for (const key of Object.keys(words) as (keyof FriendsConfig['words'])[]) {
    const w = f.words?.[key];
    if (typeof w === 'string' && w.trim()) words[key] = w;
  }
  friends = { topics: topics.length ? topics : FRIENDS_DEFAULT.topics, limits, words };
  for (const fn of friendsListeners) fn(friends);
  return friends;
}

type Answer = { values?: unknown; friends?: unknown; rev?: unknown };
const take = (body: Answer | null | undefined): ClientConfig => {
  if (typeof body?.rev === 'string') rev = body.rev;
  applyFriendsConfig(body?.friends);
  return applyClientConfig(body?.values);
};

/** Read /api/app/config once per page load; a failed read leaves the defaults (and may be asked again). */
export function loadClientConfig(): Promise<ClientConfig> {
  if (!loading) {
    loading = apiFetch<Answer>('/api/app/config')
      .then(take)
      .catch(() => {
        loading = null;
        return current;
      });
  }
  return loading;
}

/**
 * The live channel said the config changed (2026-11-01): read it again unless this is the rev in hand. The rev
 * goes on the URL so no cache on the way can answer with the old one; without one (a reconnect) it reads plainly.
 */
export function reloadClientConfig(next?: string): Promise<ClientConfig> {
  if (next && next === rev) return Promise.resolve(current);
  const url = next ? `/api/app/config?v=${encodeURIComponent(next)}` : '/api/app/config';
  loading = apiFetch<Answer>(url).then(take).catch(() => current);
  return loading;
}

/** Meet people's served words and numbers, re-rendering when a read lands. */
export function useFriendsConfig(): FriendsConfig {
  const [config, setConfig] = useState<FriendsConfig>(friends);
  useEffect(() => {
    friendsListeners.add(setConfig);
    if (config !== friends) setConfig(friends);
    return () => { friendsListeners.delete(setConfig); };
  }, []);
  return config;
}

/** The values in force, re-rendering when the read lands. */
export function useClientConfig(): ClientConfig {
  const [config, setConfig] = useState<ClientConfig>(current);
  useEffect(() => {
    listeners.add(setConfig);
    if (config !== current) setConfig(current);
    return () => { listeners.delete(setConfig); };
  }, []);
  return config;
}
